import React, { useState } from 'react';
import { Lock, Globe, Users, Eye, Shield, Plus, Trash2, Settings, Mic, Upload, AlertCircle } from 'lucide-react';
import UserRolesManager from '../../admin/UserRolesManager';

export const PrivacySection = ({ formData, bookId, onInputChange, templateType }) => {
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState({
    management: false,
    voiceRecording: false,
    uploads: false
  });
  const [emailError, setEmailError] = useState('');
  
  const viewAccess = formData.viewAccess || {
    enablePublicView: true,
    enableManagementView: true,
    allowViewToggle: true,
    defaultView: 'public',
    requireAuthForManagement: true
  };

  // Authorized users with permissions structure
  const authorizedUsers = formData.authorizedUsers || {};

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addAuthorizedUser = () => {
    setEmailError('');
    
    if (!newUserEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!validateEmail(newUserEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (authorizedUsers[newUserEmail.trim()]) {
      setEmailError('This email is already authorized');
      return;
    }

    // Check if at least one permission is selected
    if (!selectedPermissions.management && !selectedPermissions.voiceRecording && !selectedPermissions.uploads) {
      setEmailError('Please select at least one feature permission');
      return;
    }

    const updatedUsers = {
      ...authorizedUsers,
      [newUserEmail.trim()]: {
        email: newUserEmail.trim(),
        permissions: { ...selectedPermissions },
        addedAt: new Date().toISOString()
      }
    };

    onInputChange('authorizedUsers', updatedUsers);
    setNewUserEmail('');
    setSelectedPermissions({
      management: false,
      voiceRecording: false,
      uploads: false
    });
  };

  const removeAuthorizedUser = (email) => {
    const updatedUsers = { ...authorizedUsers };
    delete updatedUsers[email];
    onInputChange('authorizedUsers', updatedUsers);
  };

  const updateUserPermissions = (email, permission, value) => {
    const updatedUsers = {
      ...authorizedUsers,
      [email]: {
        ...authorizedUsers[email],
        permissions: {
          ...authorizedUsers[email].permissions,
          [permission]: value
        }
      }
    };
    onInputChange('authorizedUsers', updatedUsers);
  };

  const featurePermissions = [
    { 
      key: 'management', 
      label: 'Management View', 
      description: 'Access to admin dashboard and settings',
      icon: Settings 
    },
    { 
      key: 'voiceRecording', 
      label: 'Voice Recording', 
      description: 'Record and upload voice messages',
      icon: Mic 
    },
    { 
      key: 'uploads', 
      label: 'File Uploads', 
      description: 'Upload photos, videos, and documents',
      icon: Upload 
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Privacy & Access</h3>
        <p className="text-gray-600">
          Control who can access your {templateType} and configure feature permissions
        </p>
      </div>

      {/* Access Level */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Access Level</h4>
        <div className="space-y-3">
          {[
            { 
              value: 'public', 
              label: 'Public', 
              description: 'Anyone can view your page', 
              icon: Globe 
            },
            { 
              value: 'private', 
              label: 'Private', 
              description: 'Only authorized users can access', 
              icon: Lock 
            },
            { 
              value: 'password', 
              label: 'Password Protected', 
              description: 'Requires password to access', 
              icon: Shield 
            }
          ].map(level => {
            const IconComponent = level.icon;
            return (
              <div
                key={level.value}
                onClick={() => onInputChange('accessLevel', level.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  formData.accessLevel === level.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <IconComponent className={`h-5 w-5 mt-0.5 ${
                    formData.accessLevel === level.value ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{level.label}</div>
                    <div className="text-sm text-gray-500">{level.description}</div>
                  </div>
                  {formData.accessLevel === level.value && (
                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Password Field (if password protected) */}
      {formData.accessLevel === 'password' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Access Password
          </label>
          <input
            type="text"
            value={formData.accessPassword || ''}
            onChange={(e) => onInputChange('accessPassword', e.target.value)}
            placeholder="Enter access password..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Authorized Users Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Authorized Users</h4>
        </div>
        
        <p className="text-sm text-gray-600 mb-6">
          {formData.accessLevel === 'public' 
            ? 'Grant specific users access to exclusive features even on your public page'
            : 'Manage users who can access your private page and their feature permissions'
          }
        </p>

        {/* Add New User Form */}
        <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
          <h5 className="font-medium text-gray-900 mb-4">Add Authorized User</h5>
          
          {/* Email Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => {
                setNewUserEmail(e.target.value);
                setEmailError('');
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAuthorizedUser();
                }
              }}
              placeholder="user@example.com"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                emailError ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {emailError}
              </p>
            )}
          </div>

          {/* Feature Permissions */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Grant Access To
            </label>
            <div className="space-y-2">
              {featurePermissions.map(feature => {
                const FeatureIcon = feature.icon;
                return (
                  <label 
                    key={feature.key}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPermissions[feature.key]}
                      onChange={(e) => setSelectedPermissions({
                        ...selectedPermissions,
                        [feature.key]: e.target.checked
                      })}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <FeatureIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{feature.label}</div>
                      <div className="text-sm text-gray-500">{feature.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Add Button */}
          <button
            onClick={addAuthorizedUser}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
        </div>

        {/* Authorized Users List */}
        {Object.keys(authorizedUsers).length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">
                {Object.keys(authorizedUsers).length} User{Object.keys(authorizedUsers).length !== 1 ? 's' : ''} Authorized
              </span>
            </div>
            
            {Object.entries(authorizedUsers).map(([email, userData]) => (
              <div key={email} className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{email}</div>
                      <div className="text-xs text-gray-500">
                        Added {new Date(userData.addedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeAuthorizedUser(email)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                    title="Remove user"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* User Permissions */}
                <div className="space-y-2 pl-13">
                  {featurePermissions.map(feature => {
                    const FeatureIcon = feature.icon;
                    return (
                      <label 
                        key={feature.key}
                        className="flex items-center gap-2 text-sm cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={userData.permissions[feature.key] || false}
                          onChange={(e) => updateUserPermissions(email, feature.key, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <FeatureIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        <span className="text-gray-700 group-hover:text-gray-900">
                          {feature.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              No authorized users yet. Add users above to grant them access.
            </p>
          </div>
        )}
      </div>

      <UserRolesManager 
        bookId={bookId} 
        bookName={formData?.botName || formData?.title}
        onRoleChange={() => {
          // Optional: trigger a refresh or show success message
          console.log('Role updated');
        }}
      />

      {/* View Access Controls */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Eye className="h-5 w-5" />
          View Access Controls
        </h4>
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          
          {/* Available Views */}
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={viewAccess.enablePublicView}
                onChange={(e) => onInputChange('viewAccess', { 
                  ...viewAccess, 
                  enablePublicView: e.target.checked 
                })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900">Enable Public View</span>
                <p className="text-sm text-gray-500">Allow users to view public information</p>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={viewAccess.enableManagementView}
                onChange={(e) => onInputChange('viewAccess', { 
                  ...viewAccess, 
                  enableManagementView: e.target.checked 
                })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-gray-900">Enable Management View</span>
                <p className="text-sm text-gray-500">Allow authorized users to access management features</p>
              </div>
            </label>

            {viewAccess.enablePublicView && viewAccess.enableManagementView && (
              <label className="flex items-center gap-3 ml-6">
                <input
                  type="checkbox"
                  checked={viewAccess.allowViewToggle}
                  onChange={(e) => onInputChange('viewAccess', { 
                    ...viewAccess, 
                    allowViewToggle: e.target.checked 
                  })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Allow View Toggle</span>
                  <p className="text-sm text-gray-500">Let users switch between views</p>
                </div>
              </label>
            )}
          </div>

          {/* Default View */}
          {viewAccess.enablePublicView && viewAccess.enableManagementView && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default View
              </label>
              <select
                value={viewAccess.defaultView}
                onChange={(e) => onInputChange('viewAccess', { 
                  ...viewAccess, 
                  defaultView: e.target.value 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="public">Public View</option>
                <option value="management">Management View</option>
              </select>
            </div>
          )}

          {/* Authentication Requirement */}
          {viewAccess.enableManagementView && (
            <div className="pt-4 border-t border-gray-200">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={viewAccess.requireAuthForManagement}
                  onChange={(e) => onInputChange('viewAccess', { 
                    ...viewAccess, 
                    requireAuthForManagement: e.target.checked 
                  })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-900">Require Authentication for Management</span>
                  <p className="text-sm text-gray-500">Users must be logged in and authorized to access management features</p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Privacy Settings</h4>
        <div className="space-y-4">
          {[
            { key: 'allowAnonymousViews', label: 'Allow Anonymous Views', desc: 'Allow visitors to view without signing in' },
            { key: 'requireLoginForContact', label: 'Require Login for Contact', desc: 'Users must login to use contact forms' },
            { key: 'moderateSubmissions', label: 'Moderate Submissions', desc: 'Review form submissions before publishing' },
            { key: 'published', label: 'Published', desc: 'Make this page live and accessible' }
          ].map(({ key, label, desc }) => (
            <label key={key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={formData[key] || false}
                onChange={(e) => onInputChange(key, e.target.checked)}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <div className="font-medium text-gray-900">{label}</div>
                <div className="text-sm text-gray-500">{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};